import { useTranslate } from '@tolgee/react';
import React, { FC, useEffect, useRef } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDispatch, useSelector } from 'react-redux';
import {
  deleteTextCaption,
  getMoreCaptions,
  setCopiedCaption,
  setCurrentPlatform,
  triggerCreatorQuillInit
} from '../../../../../redux/slices/textBlocks';
import ContentCard from '../cards/contentCard';
import { addCreatorText } from '../../../../../redux/slices/lomavisCreator/lomavisCreator';
import { lomavisCreatorRootState } from '../../../../../pages/lomavisCreator';
import '../index.css';
import { lomavisShowToastr } from '../../../../../utils/ui/toastr';
import Delta from 'quill-delta';
import { ALL } from '../../../../../config/constants';
import {
  highlightQuillInput,
  trimTrailingNewline
} from '../../creatorTextEditor/config';
import { Button } from 'react-bootstrap';

interface textBlockProps {
  createCaption: () => void;
}

const TextBlocks: FC<textBlockProps> = ({ createCaption }) => {
  const { t } = useTranslate();
  const dispatch = useDispatch<any>();

  const scrollRef = useRef<HTMLDivElement>(null);

  const {
    textBlockCaptions,
    nextCaptions,
    textBlockCount,
    textBlockModal,
    loadingCaptions,
    currentCursorPositions
  } = useSelector((state: any) => state.textBlocks);

  const { cloudUuid, locationUuid, quillEditors, platformEnabled } =
    useSelector((state: lomavisCreatorRootState) => state.lomavisCreator);

  const useInCreator = (block: any) => {
    const editor = quillEditors[textBlockModal?.platform];

    // Choose the freshest cursor position available (live selection → dataset cache → redux → end)
    const liveSelection = editor?.getSelection?.();
    const datasetCursor = editor?.container?.dataset?.cursorPosition;
    const parsedDatasetCursor = Number.isFinite(Number(datasetCursor))
      ? Number(datasetCursor)
      : null;
    const storedCursor = currentCursorPositions[textBlockModal?.platform];

    let insertIndex =
      (liveSelection ? liveSelection.index + liveSelection.length : null) ??
      parsedDatasetCursor ??
      (typeof storedCursor === 'number' ? storedCursor : null) ??
      editor?.getLength?.() - 1;

    if (typeof insertIndex !== 'number' || Number.isNaN(insertIndex)) {
      insertIndex = 0;
    }

    const maxIndex = Math.max((editor?.getLength?.() || 1) - 1, 0);
    insertIndex = Math.min(Math.max(insertIndex, 0), maxIndex);

    function filterDeltaMentionsByPlatform(rawDelta, allowedPlatform) {
      const normalizePlatforms = (value: any): string[] => {
        if (Array.isArray(value)) return value.map((v) => String(v).trim());
        if (typeof value === 'string') {
          return value
            .split(',')
            .map((v) => v.trim())
            .filter(Boolean);
        }
        return [];
      };

      const filteredOps = rawDelta.ops.filter((op) => {
        if (typeof op.insert === 'object' && op.insert.mention) {
          const mentionPlatforms = normalizePlatforms(op.insert.mention.platform);

          // Keep mentions when the current platform is explicitly allowed,
          // or when the mention has no platform metadata at all.
          const matchesPlatform =
            mentionPlatforms.length === 0 ||
            mentionPlatforms.includes(allowedPlatform);

          return matchesPlatform;
        }

        return true;
      });

      return { ops: filteredOps };
    }

    try {
      // Prevent accidental double inserts (you can use a ref to track once-run status if needed)
      const rawDelta =
        textBlockModal?.platform !== ALL
          ? filterDeltaMentionsByPlatform(
              block.text_quill,
              textBlockModal?.platform
            )
          : block.text_quill;

      if (
        !rawDelta ||
        (typeof rawDelta !== 'string' && !Array.isArray(rawDelta.ops))
      ) {
        console.log('Skipping insert: invalid platformPostData.text', rawDelta);
        return;
      }

      let deltaToInsert = new Delta(rawDelta);
      deltaToInsert = trimTrailingNewline(deltaToInsert);

      const finalDelta = new Delta().retain(insertIndex).concat(deltaToInsert);

      editor.updateContents(finalDelta, 'user');

      // Move cursor
      const insertedLength = deltaToInsert.length();
      const finalCursor = Math.min(
        insertIndex + insertedLength,
        maxIndex + insertedLength
      );
      editor.setSelection(finalCursor, 0, 'silent');

      if (
        textBlockModal?.platform !== ALL &&
        platformEnabled[textBlockModal?.platform]
      ) {
        highlightQuillInput(editor);
      }
    } catch (err) {
      console.error('Unexpected error in inserting content:', err);
    }

    lomavisShowToastr(
      t('lomavisPostApproval.textBlock.success_captionInserted'),
      'success'
    );
  };

  const copyCaption = (block: any) => {
    const quillDelta = {
      __quillCustom: true,
      ...block?.text_quill
    };

    navigator.clipboard
      .writeText(JSON.stringify(quillDelta))
      .catch((err) => console.error('Failed to copy:', err));
  };

  // 👇 Auto-trigger loading more captions if content doesn't fill scroll area
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const contentIsShort = container.scrollHeight <= container.clientHeight;

    if (contentIsShort && nextCaptions) {
      dispatch(getMoreCaptions(nextCaptions));
    }
  }, [textBlockCaptions, nextCaptions]);

  return (
    <div
      className="w-100 mt-3"
      id="scrollableDiv"
      ref={scrollRef}
      style={{
        height: '100%',
        overflowY: 'auto',
        maxHeight: 'calc(100vh - 260px)'
      }}
    >
      <InfiniteScroll
        className="w-100 h-100"
        dataLength={textBlockCaptions?.length}
        style={{ overflow: 'visible', height: '100%' }}
        next={() => {
          dispatch(getMoreCaptions(nextCaptions));
        }}
        hasMore={!!nextCaptions}
        inverse={false}
        loader={
          <div className="text-center my-2">
            <span
              className="spinner-border"
              role="status"
              aria-hidden="true"
            ></span>
          </div>
        }
        scrollableTarget="scrollableDiv"
      >
        {loadingCaptions ? (
          // Show skeletons while loading
          Array.from({ length: 10 }).map((_, index) => (
            <div
              key={index}
              className="skeleton-loader w-100 mb-3 rounded"
              style={{ height: '120px' }}
            ></div>
          ))
        ) : textBlockCaptions?.length === 0 ? (
          // Empty state after loading
          <div className="w-100 mt-6 d-flex flex-column gap-4 justify-content-center align-items-center">
            <p>{t('lomavisCreator.textBlock.noCaption')} </p>
            <Button
              className="d-flex align-items-center fs-4"
              size="sm"
              variant="primary"
              onClick={() => {
                createCaption();
              }}
            >
              <i className="fa-duotone fa-solid fa-plus"></i>{' '}
              {t('lomavisCreator.textBlock.create')}
            </Button>
          </div>
        ) : (
          textBlockCaptions?.map((block) => (
            <ContentCard
              key={block?.uuid}
              title={block?.title}
              text={block?.text_as_html}
              quillText={block?.text_quill}
              block={block}
              copyCaption={() => copyCaption(block)}
              insertIntoText={() => useInCreator(block)}
              deleteFolder={() =>
                dispatch(
                  deleteTextCaption(
                    locationUuid,
                    cloudUuid,
                    {
                      uuid: block?.uuid
                    },
                    () => {
                      lomavisShowToastr(
                        t(
                          'lomavisPostApproval.textBlock.success_captionDeleted'
                        ),
                        'success'
                      );
                    },

                    () => {
                      lomavisShowToastr(
                        t('lomavisPostApproval.textBlock.err_captionDeleted'),
                        'error'
                      );
                    }
                  )
                )
              }
            />
          ))
        )}
      </InfiniteScroll>
    </div>
  );
};

export default TextBlocks;
